/**
 * HomePage - Main earnings search page using Simulacra design system
 */
import React, { useState, useEffect } from 'react';
import { YStack, XStack, H1, Text, Spinner } from 'tamagui';
import { GlassCard, MatrixButton } from '../components/base';
import TickerDropdown from '../components/TickerDropdown';
import PopularTickers from '../components/PopularTickers';
import EarningsSummary from '../components/EarningsSummary';

const API_URL = 'https://nings-api.beamer-labs.workers.dev';

interface HomePageProps {
  isLoggedIn: boolean;
  accessToken: string | null;
  onLogout: () => void;
}

export const HomePage: React.FC<HomePageProps> = ({ isLoggedIn, accessToken, onLogout }) => {
  const [ticker, setTicker] = useState('');
  const [loading, setLoading] = useState(false);
  const [earnings, setEarnings] = useState<any>(null);
  const [error, setError] = useState('');

  // Read ticker from URL on component mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlTicker = params.get('ticker');
    if (urlTicker) {
      setTicker(urlTicker.toUpperCase());
      fetchEarningsForTicker(urlTicker.toUpperCase());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEarningsForTicker = async (tickerSymbol: string) => {
    if (!tickerSymbol.trim()) return;

    setLoading(true);
    setError('');
    setEarnings(null);

    try {
      const headers: any = {};
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const response = await fetch(
        `${API_URL}/earnings?ticker=${tickerSymbol.trim().toUpperCase()}`,
        { headers }
      );

      const data = await response.json();

      if (response.status === 401) {
        setError('Session expired. Please log in again.');
        onLogout();
        return;
      }

      if (response.ok) {
        setEarnings(data);
      } else {
        setError(data.detail?.message || data.detail || 'Failed to fetch earnings');
      }
    } catch (err) {
      setError('Failed to connect to the API');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticker.trim()) return;

    const url = new URL(window.location.href);
    url.searchParams.set('ticker', ticker.toUpperCase());
    window.history.pushState({}, '', url);

    fetchEarningsForTicker(ticker);
  };

  const handleTickerSelect = (selectedTicker: string) => {
    setTicker(selectedTicker);
    const url = new URL(window.location.href);
    url.searchParams.set('ticker', selectedTicker.toUpperCase());
    window.history.pushState({}, '', url);
    fetchEarningsForTicker(selectedTicker);
  };

  // Extract grade from the summary text
  const extractGrade = (summary: string): string | null => {
    // Look for patterns like "**Overall Grade: X**" or "Overall Grade: X"
    const gradeMatch = summary.match(/\*\*Overall Grade:\s*([^*\n]+)\*\*/i) ||
                      summary.match(/Overall Grade:\s*([^\n]+)/i);
    return gradeMatch ? gradeMatch[1].trim() : null;
  };

  const getGradeColor = (grade: string): string => {
    const gradeLower = grade.toLowerCase();
    if (gradeLower.includes('incredible') || gradeLower.includes('a+') || gradeLower.includes('a-')) return '$cyan10';
    if (gradeLower.includes('good') || gradeLower.includes('b')) return '$green10';
    if (gradeLower.includes('neutral') || gradeLower.includes('c')) return '$color';
    if (gradeLower.includes('bad') || gradeLower.includes('d')) return '$orange10';
    if (gradeLower.includes('horrible') || gradeLower.includes('f')) return '$red10';
    return '$color';
  };

  return (
    <YStack flex={1} padding="$4" gap="$6" maxWidth={1200} marginHorizontal="auto" width="100%">
      {/* Welcome header */}
      {!earnings && (
        <YStack gap="$3" alignItems="center" paddingVertical="$4">
          <H1 color="$green10" fontFamily="$heading" textAlign="center">
            NINGS Stock Earnings Analyzer
          </H1>
          <Text color="$color" fontFamily="$body" fontSize="$4" textAlign="center">
            Get AI-powered earnings summaries for any stock ticker
          </Text>
        </YStack>
      )}

      {/* Search form */}
      <GlassCard glowOnHover={false} backgroundColor="$backgroundPress">
        <form onSubmit={handleSubmit}>
          <XStack gap="$3" flexWrap="wrap" alignItems="flex-end">
            <YStack flex={1} minWidth={250}>
              <TickerDropdown
                value={ticker}
                onChange={setTicker}
                onSelect={handleTickerSelect}
                disabled={loading}
                placeholder="Enter ticker (e.g., AAPL)"
              />
            </YStack>
            <MatrixButton
              variant="primary"
              disabled={loading || !ticker.trim()}
              onPress={handleSubmit}
            >
              {loading ? 'Loading...' : 'Fetch Earnings'}
            </MatrixButton>
          </XStack>
        </form>
      </GlassCard>

      {/* Error message */}
      {error && (
        <GlassCard backgroundColor="$backgroundPress" borderColor="$red10" borderWidth={2}>
          <Text color="$red10" fontFamily="$mono">
            {error}
          </Text>
        </GlassCard>
      )}

      {/* Loading state */}
      {loading && (
        <YStack alignItems="center" gap="$4" paddingVertical="$6">
          <Spinner size="large" color="$green10" />
          <Text color="$green10" fontFamily="$mono">
            Analyzing earnings...
          </Text>
        </YStack>
      )}

      {/* Earnings results */}
      {earnings && !loading && (
        isLoggedIn ? (
          <EarningsSummary ticker={earnings.ticker} summary={earnings.summary} />
        ) : (
          <YStack gap="$4">
            <GlassCard>
              <YStack gap="$4" alignItems="center" paddingVertical="$6">
                <Text fontSize="$2" color="$placeholderColor" fontFamily="$mono" textTransform="uppercase">
                  Earnings Grade
                </Text>
                {extractGrade(earnings.summary) ? (
                  <Text
                    fontSize="$9"
                    fontWeight="bold"
                    color={getGradeColor(extractGrade(earnings.summary)!)}
                    fontFamily="$heading"
                  >
                    {extractGrade(earnings.summary)}
                  </Text>
                ) : (
                  <Text fontSize="$5" color="$color" fontFamily="$body" textAlign="center">
                    Grade not available
                  </Text>
                )}
                <Text fontSize="$3" color="$colorPress" fontFamily="$body" textAlign="center" paddingTop="$4">
                  For ${earnings.ticker}
                </Text>
              </YStack>
            </GlassCard>
            <GlassCard backgroundColor="$backgroundHover" borderColor="$green10" borderWidth={1}>
              <YStack gap="$3" alignItems="center" paddingVertical="$4">
                <Text fontSize="$4" color="$color" fontFamily="$heading" textAlign="center">
                  Want the full analysis?
                </Text>
                <Text fontSize="$3" color="$colorPress" fontFamily="$body" textAlign="center">
                  Sign in to see detailed earnings summaries, key highlights, and more.
                </Text>
                <MatrixButton
                  variant="primary"
                  onPress={() => window.location.href = '/login'}
                >
                  Sign In
                </MatrixButton>
              </YStack>
            </GlassCard>
          </YStack>
        )
      )}

      {/* Popular tickers grid */}
      {!earnings && !loading && <PopularTickers onSelectTicker={handleTickerSelect} />}
    </YStack>
  );
};

export default HomePage;
